import type { UserHolding } from '@/helpers/holdings'

/** One chain's holdings fetch outcome, tagged with the chain it belongs to. */
export type ChainHoldingsResult =
  | { chainId: number; status: 'fulfilled'; holdings: UserHolding[] }
  | { chainId: number; status: 'rejected'; reason: unknown }

export interface FailedChainHolding {
  chainId: number
  reason: unknown
}

export interface PartitionedHoldings {
  /** Flattened holdings from every chain whose fetch succeeded. */
  holdings: UserHolding[]
  /** One entry per chain whose fetch failed, carrying the reason for logging. */
  failures: FailedChainHolding[]
  /**
   * True when a chain failed AND the chains that succeeded returned no holdings,
   * so we cannot tell "the wallet holds nothing" from "its holdings are only on
   * the failed chain". The caller surfaces this as an error/unknown rather than
   * a misleading empty state. Covers a total outage (every chain failed) too.
   */
  isIndeterminate: boolean
}

/**
 * Partitions the per-chain holdings results into the successful holdings and
 * the chains that failed. Each result carries its own `chainId`, so failed
 * chains stay correctly attributed without a parallel array. A partial outage
 * with surviving holdings degrades gracefully; only an indeterminate result
 * surfaces an error (see `isIndeterminate`). See VAU-1227.
 */
export function partitionChainHoldings(results: ChainHoldingsResult[]): PartitionedHoldings {
  const holdings: UserHolding[] = []
  const failures: FailedChainHolding[] = []

  for (const result of results) {
    if (result.status === 'fulfilled') {
      holdings.push(...result.holdings)
    } else {
      failures.push({ chainId: result.chainId, reason: result.reason })
    }
  }

  return {
    holdings,
    failures,
    isIndeterminate: failures.length > 0 && holdings.length === 0
  }
}

/**
 * Deduplicates token ids case-insensitively while preserving canonical casing.
 */
export function normalizeHoldingsTokenIds(
  holdings: UserHolding[] | undefined
): string[] | undefined {
  if (!holdings) return undefined

  const tokenIdsByLowercase = new Map<string, string>()

  for (const holding of holdings) {
    const tokenId = holding.token.id
    const tokenIdLowercase = tokenId.toLowerCase()

    if (!tokenIdsByLowercase.has(tokenIdLowercase)) {
      tokenIdsByLowercase.set(tokenIdLowercase, tokenId)
    }
  }

  return [...tokenIdsByLowercase.values()]
}
